import React, { ReactElement } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  createPlanningCourseV2,
  fetchPlanningCoursesV2,
  selectPlanningSections,
  selectPlanningTemplates,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'

type Props = {
  open: boolean
  onClose: () => void
  onCreated: (courseId: string) => void
}

export default function CourseCreationModal({
  open,
  onClose,
  onCreated,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let sections = useAppSelector(selectPlanningSections)
  let templates = useAppSelector(selectPlanningTemplates)

  let form = useForm({
    defaultValues: {
      name: '',
      sectionID: '',
      templateID: '',
      length: 1,
    },
    onSubmit: async ({ value }) => {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      let result = await dispatch(
        createPlanningCourseV2({
          name: value.name.trim(),
          sectionID: value.sectionID,
          templateID: value.templateID,
          length: Number(value.length),
        }),
      )
      if (createPlanningCourseV2.fulfilled.match(result)) {
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchPlanningCoursesV2())
        onCreated(result.payload.id)
        form.reset()
      }
    },
  })

  let handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: modalPaperProps,
      }}
      maxWidth="sm"
    >
      <DialogTitle>Create Enduring Course</DialogTitle>
      <DialogContent>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            mb: 3,
          }}
        >
          New courses are created as Course v2 and scoped to FOAMfrat (no linked
          service). All fields can be edited after creation.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <form.Field
            name="name"
            children={(field) => (
              <TextField
                label="Course Name *"
                fullWidth
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                sx={modalInputSx}
              />
            )}
          />

          <form.Field
            name="length"
            children={(field) => (
              <TextField
                label="CE Hours *"
                fullWidth
                type="number"
                value={field.state.value}
                onChange={(e) => field.handleChange(Number(e.target.value))}
                onBlur={field.handleBlur}
                sx={modalInputSx}
                slotProps={{
                  htmlInput: { min: 0.5, step: 0.5 },
                }}
              />
            )}
          />

          <form.Field
            name="sectionID"
            children={(field) => (
              <FormControl fullWidth>
                <InputLabel>Section (Category) *</InputLabel>
                <Select
                  label="Section (Category) *"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  sx={modalInputSx}
                >
                  {sections.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <form.Field
            name="templateID"
            children={(field) => (
              <FormControl fullWidth>
                <InputLabel>Template *</InputLabel>
                <Select
                  label="Template *"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  sx={modalInputSx}
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <form.Subscribe
          selector={(s) => [s.isSubmitting, s.values]}
          children={([isSubmitting, values]) => {
            let v = values as typeof form.state.values
            let canSubmit =
              !isSubmitting &&
              v.name.trim().length > 0 &&
              v.sectionID.length > 0 &&
              v.templateID.length > 0 &&
              v.length > 0
            return (
              <Button
                variant="contained"
                onClick={() => form.handleSubmit()}
                disabled={!canSubmit}
              >
                {isSubmitting ? 'Creating…' : 'Create Course'}
              </Button>
            )
          }}
        />
      </DialogActions>
    </Dialog>
  )
}
